import { INavConfig, getNavConfig } from '@/services/system-config-service';
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { NavBoardDraft } from '../functions/nav-board-draft';

const initialState: NavBoardDataSlice = {
  loading: false,
  canPublish: false,
  board: {},
  sorted: [],
};

const dataSlice = createSlice({
  name: 'navBoard/data',
  initialState: initialState,
  reducers: {
    updateNavBoardDataGenericReducer: (state, action: PayloadAction<Partial<NavBoardDataSlice>>) => {
      const { loading, canPublish, board, sorted } = action.payload;
      state.loading = loading !== undefined ? loading : state.loading;
      state.board = board !== undefined ? board : state.board;
      state.sorted = sorted !== undefined ? sorted : state.sorted;

      if (canPublish === undefined && (board !== undefined || sorted !== undefined)) state.canPublish = true;
      else state.canPublish = canPublish !== undefined ? canPublish : state.canPublish;

      if (state.canPublish === false) NavBoardDraft.Delete();
      else NavBoardDraft.Set({ board: state.board, sorted: state.sorted });
    },
    createNavGroup: (state, action: PayloadAction<INavConfig>) => {
      state.sorted.push(action.payload);
      state.board = { ...state.board, [action.payload.key]: [] };
      state.canPublish = true;
      NavBoardDraft.Set({ board: state.board, sorted: state.sorted });
    },
    updateNavGroup: (state, action: PayloadAction<{ selectedNavGroupKey: INavConfig['key']; data: Pick<INavConfig, 'title' | 'description' | 'summary' | 'icon'> }>) => {
      state.canPublish = true;
      state.sorted = state.sorted.map((item) => {
        if (item.key === action.payload.selectedNavGroupKey) {
          item.title = action.payload.data.title;
          item.description = action.payload.data.description;
          item.summary = action.payload.data.summary;
          item.icon = action.payload.data.icon;
        }
        return item;
      });
      NavBoardDraft.Set({ board: state.board, sorted: state.sorted });
    },
    deleteNavGroup: (state, action: PayloadAction<INavConfig['key']>) => {
      state.sorted = state.sorted.filter((item) => item.key !== action.payload);
      delete state.board[action.payload];
      state.canPublish = true;
      NavBoardDraft.Set({ board: state.board, sorted: state.sorted });
    },
    createRouteGroup: (state, action: PayloadAction<{ navGroupId: INavConfig['key']; routeGroup: INavConfig }>) => {
      state.board[action.payload.navGroupId].push(action.payload.routeGroup);
      state.canPublish = true;
      NavBoardDraft.Set({ board: state.board, sorted: state.sorted });
    },
    updateRouteGroup: (state, action: PayloadAction<{ navGroupId: INavConfig['key']; routeGroup: INavConfig }>) => {
      state.board[action.payload.navGroupId] = state.board[action.payload.navGroupId].map((item) => (item.key === action.payload.routeGroup.key ? action.payload.routeGroup : item));
      state.canPublish = true;
      NavBoardDraft.Set({ board: state.board, sorted: state.sorted });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getNavConfigThunk.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getNavConfigThunk.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload === null) {
        state.board = {};
        state.sorted = [];
        state.canPublish = false;
      } else {
        const draftedData = NavBoardDraft.Get();
        const responseData = action.payload;

        state.canPublish = Boolean(draftedData !== null);
        state.sorted = draftedData !== null ? draftedData.sorted : responseData.filter((item) => item.type === 'title');
        if (draftedData !== null) state.board = draftedData.board;
        else responseData.forEach((item) => (state.board[item.key] = item.subMenu));
      }
    });
  },
});

export const { updateNavBoardDataGenericReducer, createNavGroup, updateRouteGroup, updateNavGroup, deleteNavGroup, createRouteGroup } = dataSlice.actions;
export default dataSlice.reducer;

export interface NavBoardDataSlice {
  loading: boolean;
  canPublish: boolean;
  board: Record<INavConfig['key'], INavConfig['subMenu']>;
  sorted: INavConfig[];
}

export const getNavConfigThunk = createAsyncThunk('navBoard/getNavDataThunk', async () => {
  const response = await getNavConfig();
  return response.data ? response.data[0].data : [];
});
